import { DistributionChannelService } from '@src/services/DistributionChannelService';
import { loadSpeakerLabelToFaceLabels } from '@src/services/framing/loadSpeakerLabelToFaceLabels';
import { TranslationMemoryCollectionService } from '@src/services/TranslationMemoryCollectionService';
import { TranslationMemoryEntryService } from '@src/services/TranslationMemoryEntryService';
import { TranslationMemoryImportService } from '@src/services/TranslationMemoryImportService';
import { makeAutoObservable } from 'mobx';

import { EditorStore } from '../editor/EditorStore';
import { FolderService } from '../services/FolderService';
import { setAuthTokenGetter } from '../utility/authenticatedFetch';
import { AnalyticsStore } from './AnalyticsStore';
import { BRollStore } from './BRollStore';
import { ClipPreviewStore } from './ClipPreviewStore';
import { ContentStudioUIStore } from './ContentStudioUIStore';
import { DashboardStore } from './DashboardStore';
import { DatoClientStore } from './DatoClientStore';
import { DatoPersonStore } from './DatoPersonStore';
import { DistributionChannelsStore } from './DistributionChannelsStore';
import { DubbingStore } from './DubbingStore';
import { FeatureFlagsStore } from './FeatureFlagsStore';
import { FolderStructureStore } from './FolderStructureStore';
import { KaraokeStore } from './KaraokeStore';
import { LanguageStore } from './LanguageStore';
import { LayoutStore } from './LayoutStore';
import { PublishRecordsStore } from './PublishRecordsStore';
import { PublishTrackingStore } from './PublishTrackingStore';
import { SceneLayoutIndexStore } from './SceneLayoutIndexStore';
import { SpeakerFaceMapStore } from './SpeakerFaceMapStore';
import { StudioHandoffStore } from './StudioHandoffStore';
import { TemplatedPromptsStore } from './TemplatedPromptsStore';
import { TranscriptionStore } from './TranscriptionStore';
import { TranslationHandlerStore } from './TranslationHandlerStore';
import { TranslationMemoryEntryStore } from './TranslationMemoryEntryStore';
import { TranslationMemoryImportStore } from './TranslationMemoryImportStore';
import { TranslationMemoryStore } from './TranslationMemoryStore';
import { TranslationStore } from './TranslationStore';
import { UserIdentityStore } from './UserIdentityStore';
import { VideoSourceStore } from './VideoSourceStore';
import { WhatsNewStore } from './WhatsNewStore';
import { wireTranslationPhraseResolver } from './wireTranslationPhraseResolver';
import { WriteQueueStore } from './WriteQueueStore';

import type { DatoContext } from './types';

interface InitializeParams {
  env: string;
  datoContext?: DatoContext;
}

export class RootStore {
  // Child Stores
  userIdentityStore: UserIdentityStore;
  analyticsStore: AnalyticsStore;
  templatedPromptsStore: TemplatedPromptsStore;
  datoClientStore: DatoClientStore;
  transcriptionStore: TranscriptionStore;
  dashboardStore: DashboardStore;
  editorStore: EditorStore;
  videoSourceStore: VideoSourceStore;
  bRollStore: BRollStore;
  datoPersonStore: DatoPersonStore;
  layoutStore: LayoutStore;
  karaokeStore: KaraokeStore;
  languageStore: LanguageStore;
  translationHandlerStore: TranslationHandlerStore;
  translationStore: TranslationStore;
  sceneLayoutIndexStore: SceneLayoutIndexStore;
  writeQueueStore: WriteQueueStore;
  clipPreviewStore: ClipPreviewStore;
  studioHandoffStore: StudioHandoffStore;
  whatsNewStore: WhatsNewStore;
  contentStudioUIStore: ContentStudioUIStore;
  distributionChannelsStore: DistributionChannelsStore;
  dubbingStore: DubbingStore;
  translationMemoryStore: TranslationMemoryStore;
  translationMemoryEntryStore: TranslationMemoryEntryStore;
  translationMemoryImportStore: TranslationMemoryImportStore;
  featureFlagsStore: FeatureFlagsStore;
  folderStructureStore: FolderStructureStore;
  publishTrackingStore: PublishTrackingStore;
  publishRecordsStore: PublishRecordsStore;
  speakerFaceMapStore: SpeakerFaceMapStore;

  // Base State
  initialized: boolean = false;
  storyId: string | undefined | null = null;

  constructor() {
    // Initialize child stores without dependencies
    this.userIdentityStore = new UserIdentityStore(this);
    this.analyticsStore = new AnalyticsStore(this);
    this.templatedPromptsStore = new TemplatedPromptsStore(this);
    this.datoClientStore = new DatoClientStore(this);
    this.transcriptionStore = new TranscriptionStore(this);
    this.dashboardStore = new DashboardStore(this);
    this.videoSourceStore = new VideoSourceStore(this.analyticsStore);
    this.bRollStore = new BRollStore(this);
    this.datoPersonStore = new DatoPersonStore(this);
    this.layoutStore = new LayoutStore(this);
    this.karaokeStore = new KaraokeStore();
    this.languageStore = new LanguageStore(this);
    this.translationHandlerStore = new TranslationHandlerStore(this);
    this.translationStore = new TranslationStore(this);
    this.sceneLayoutIndexStore = new SceneLayoutIndexStore();
    this.speakerFaceMapStore = new SpeakerFaceMapStore(
      loadSpeakerLabelToFaceLabels,
    );
    this.editorStore = new EditorStore(
      this.videoSourceStore,
      this.transcriptionStore,
      this.karaokeStore,
      this.sceneLayoutIndexStore,
      () => this.speakerFaceMapForFraming,
    );
    // Wire the captions translation phrase resolver onto the story-level
    // editor for its whole lifetime — the resolver reads the shared
    // translationStore on each call, so one wiring covers translations that
    // hydrate later. Clip-scoped editors wire their own where they're built.
    wireTranslationPhraseResolver(
      this.editorStore.editor,
      this.translationStore,
    );
    this.writeQueueStore = new WriteQueueStore(this);
    this.clipPreviewStore = new ClipPreviewStore();
    this.studioHandoffStore = new StudioHandoffStore();
    this.whatsNewStore = new WhatsNewStore(this);
    this.contentStudioUIStore = new ContentStudioUIStore();
    this.distributionChannelsStore = new DistributionChannelsStore(
      new DistributionChannelService(
        () => this.datoClientStore.distributionChannelRepository,
      ),
    );
    this.dubbingStore = new DubbingStore(this);
    this.translationMemoryStore = new TranslationMemoryStore(
      new TranslationMemoryCollectionService(
        () => this.datoClientStore.translationMemoryCollectionRepository,
      ),
    );
    this.translationMemoryEntryStore = new TranslationMemoryEntryStore(
      new TranslationMemoryEntryService(
        () => this.datoClientStore.translationMemoryEntryRepository,
      ),
    );
    this.translationMemoryImportStore = new TranslationMemoryImportStore(
      new TranslationMemoryImportService(
        () => this.datoClientStore.translationMemoryImportRepository,
        () => this.datoClientStore.translationMemoryEntryRepository,
      ),
      this.translationMemoryEntryStore,
    );
    this.featureFlagsStore = new FeatureFlagsStore();
    this.publishTrackingStore = new PublishTrackingStore(this);
    this.publishRecordsStore = new PublishRecordsStore();
    const folderApiUrl = process.env.REACT_APP_API_URL || '';
    const tokenGetter = async () =>
      (await this.userIdentityStore.getFreshToken()) || undefined;
    const folderService = new FolderService(folderApiUrl, tokenGetter);
    this.folderStructureStore = new FolderStructureStore(folderService);

    // Make the same Clerk token available to module-level authenticatedFetch
    // callers (useFaceDetections, SceneDetectionService, etc.) so they pick
    // up Authorization: Bearer … without threading the session through every
    // call stack.
    setAuthTokenGetter(tokenGetter);

    makeAutoObservable(this, {
      userIdentityStore: false,
      analyticsStore: false,
      templatedPromptsStore: false,
      datoClientStore: false,
      editorStore: false,
      videoSourceStore: false,
      bRollStore: false,
      datoPersonStore: false,
      layoutStore: false,
      karaokeStore: false,
      languageStore: false,
      translationHandlerStore: false,
      translationStore: false,
      sceneLayoutIndexStore: false,
      writeQueueStore: false,
      clipPreviewStore: false,
      studioHandoffStore: false,
      whatsNewStore: false,
      contentStudioUIStore: false,
      distributionChannelsStore: false,
      dubbingStore: false,
      translationMemoryStore: false,
      translationMemoryEntryStore: false,
      translationMemoryImportStore: false,
      featureFlagsStore: false,
      folderStructureStore: false,
      publishTrackingStore: false,
      publishRecordsStore: false,
      speakerFaceMapStore: false,
    });
  }

  // Initialize the store with the storyId, environment, and Dato context
  // This is a proper initialization sequence that ensures dependencies are initialized
  // in the correct order
  initialize({ env, datoContext }: InitializeParams) {
    // 1. Set up environment variables and core client settings
    this.userIdentityStore.setEnvironment(env);
    // 2. Set up DatoContext if provided
    if (datoContext) {
      this.userIdentityStore.setDatoContext(datoContext);
    }

    if (
      !this.userIdentityStore.datoContext?.currentUserAccessToken &&
      !this.userIdentityStore.clerkToken
    ) {
      throw new Error(
        'RootStore: refusing to initialize without authenticated user (no Dato context token and no Clerk token)',
      );
    }

    this.initialized = true;
  }

  // We're ready if we're initialized and have either:
  // 1. Dato context
  // 2. Valid environment for external users
  // 3. API clients are ready
  get isReady(): boolean {
    return (
      this.initialized &&
      (this.userIdentityStore.hasDatoContext ||
        !!this.userIdentityStore.environment) &&
      this.datoClientStore.isReady
    );
  }

  // Helper method to get current auth type
  get authType(): 'dato' | 'clerk' | null {
    if (this.userIdentityStore.hasDatoContext) return 'dato';
    if (this.userIdentityStore.currentIdentity?.source === 'clerk')
      return 'clerk';
    return null;
  }

  // Helper to check if we have valid authentication
  get hasValidAuth(): boolean {
    return this.userIdentityStore.currentIdentity !== null;
  }

  /**
   * Speaker→face map for scenes created mid-edit (restore, trim-extend), or
   * null to keep them face-only — including under the `basic`
   * `autoFramingStrategy` kill-switch.
   */
  get speakerFaceMapForFraming(): Record<string, string[]> | null {
    if (this.featureFlagsStore.flags.autoFramingStrategy === 'basic') {
      return null;
    }
    return this.speakerFaceMapStore.mapForFraming(
      this.editorStore.editor.getOriginalTranscript(),
    );
  }
}
